'use client';

import classNames from 'classnames';
import { type ComponentProps, type FC, type FormEventHandler, type ReactNode, useId } from 'react';
import { FieldFooter } from '../../atoms/field-footer';
import { ExclamationMarkTriangleXs } from '../../icons/symbols/exclamation-mark-triangle-xs';
import styles from './textfield.module.scss';

export type TextfieldProps = ComponentProps<'input'> & {
  centered?: boolean;
  className?: string;
  'data-testid'?: string;
  errorMessage?: string;
  footer?: ReactNode;
  forcedError?: string;
  inputClassName?: string;
  label: string;
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
};

const defaultOnInvalid: FormEventHandler<HTMLInputElement> = (e) => e.preventDefault();

export const Textfield: FC<TextfieldProps> = ({
  centered,
  className,
  'data-testid': testId,
  errorMessage,
  footer,
  forcedError,
  inputClassName,
  label,
  leftSlot,
  onInvalid = defaultOnInvalid,
  placeholder = ' ',
  rightSlot,
  ...props
}) => {
  const id = useId();
  const labelId = `${id}-label`;
  const footerId = footer ? `${id}-footer` : undefined;

  return (
    <div className={classNames(styles.wrapper, className)} data-testid={testId}>
      <label className={classNames(className, styles['field-wrapper'])}>
        {leftSlot && <div className={styles.slot}>{leftSlot}</div>}
        <div className={styles['input-wrapper']}>
          <div className={classNames(styles.label, centered && styles['label-centered'])} id={labelId}>
            {label}
          </div>
          <input
            {...props}
            aria-labelledby={[labelId, footerId].join(' ')}
            className={classNames(inputClassName, styles.input, centered && styles['input-centered'])}
            onInvalid={onInvalid}
            placeholder={props.type !== 'date' ? placeholder : undefined}
          />
        </div>
        {rightSlot && <div className={styles.slot}>{rightSlot}</div>}
      </label>
      {forcedError ? (
        <FieldFooter
          className={classNames(styles['error-message'], styles['forced-error'])}
          data-testid="field-error"
          error
          icon={<ExclamationMarkTriangleXs />}
        >
          {forcedError}
        </FieldFooter>
      ) : (
        <>
          {footer && (
            <FieldFooter className={classNames(styles.footer)} data-testid="field-footer" id={footerId}>
              {footer}
            </FieldFooter>
          )}
          {errorMessage && (
            <FieldFooter
              className={styles['error-message']}
              data-testid="field-error"
              error
              icon={<ExclamationMarkTriangleXs />}
            >
              {errorMessage}
            </FieldFooter>
          )}
        </>
      )}
    </div>
  );
};
